import React, { useState, useEffect } from 'react';

import { Turnstile } from '@marsidev/react-turnstile';

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

const TurnstileWithTheme = Turnstile as unknown as React.ComponentType<{
  siteKey: string;
  theme?: 'light' | 'dark' | 'auto';
  onBeforeInteractive?: () => void;
  onSuccess?: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}>;

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ onVerify, onExpire, onError }) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'verified' | 'error'>('loading');
  const isLocalhost =
    import.meta.env.DEV ||
    ['localhost', '127.0.0.1'].includes(window.location.hostname) ||
    window.location.hostname.includes('dev') ||
    window.location.hostname.includes('test') ||
    window.location.hostname.includes('staging') ||
    window.location.hostname.includes('uat');

  useEffect(() => {
    if (isLocalhost) {
      const timer = setTimeout(() => {
        setStatus('verified');
        onVerify('localhost-bypass-token');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLocalhost, onVerify]);

  return (
    <div className="w-full mt-4">
      <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-md shadow-[0_0_30px_rgba(168,85,247,0.08)] transition-all duration-500">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex gap-[3px]">
                <span className="w-[6px] h-[6px] rounded-full bg-purple-400/60" />
                <span className="w-[6px] h-[6px] rounded-full bg-pink-400/60" />
                <span className="w-[6px] h-[6px] rounded-full bg-blue-400/60" />
              </div>
              <p className="text-xs text-white/40 tracking-widest uppercase">Security Check</p>
            </div>

            {/* Status text */}
            <div className="mb-3 min-h-[20px]">
              {status === 'loading' && (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-purple-400"
                        style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-white/40">Initializing...</span>
                </div>
              )}
              {status === 'ready' && (
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                  <span className="text-xs text-white/50">Complete verification</span>
                </div>
              )}
              {status === 'verified' && (
                <div className="flex items-center gap-2">
                  <svg
                    className="w-3.5 h-3.5 text-emerald-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-xs text-emerald-400">
                    {isLocalhost ? 'Development Bypass Active' : 'Verification complete'}
                  </span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2">
                  <svg
                    className="w-3.5 h-3.5 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-xs text-red-400">Verification failed. Please retry.</span>
                </div>
              )}
            </div>
          </div>

          {/* Skeleton loader */}
          {status === 'loading' && (
            <div className="w-full h-[65px] rounded-xl bg-white/5 border border-white/5 flex items-center px-4 gap-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-white/10" />
              <div className="flex-1 space-y-2">
                <div className="h-2 bg-white/10 rounded w-3/4" />
                <div className="h-2 bg-white/10 rounded w-1/2" />
              </div>
              <div className="w-16 h-7 rounded-lg bg-white/10" />
            </div>
          )}

          {/* Turnstile widget - Hidden on localhost */}
          {!isLocalhost && (
            <div
              className={
                status === 'loading'
                  ? 'absolute opacity-0 pointer-events-none'
                  : 'flex justify-center'
              }
              style={{
                filter: 'invert(1) hue-rotate(220deg) saturate(0.6) brightness(0.9)',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <TurnstileWithTheme
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                // theme="dark"
                onBeforeInteractive={() => {
                  setStatus('ready');
                }}
                onSuccess={(token: string) => {
                  setStatus('verified');
                  onVerify(token);
                }}
                onExpire={() => {
                  console.warn('[TurnstileWidget] Token expired');
                  setStatus('ready');
                  onExpire?.();
                }}
                onError={() => {
                  console.error('[TurnstileWidget] Widget error');
                  setStatus('error');
                  onError?.();
                }}
              />
            </div>
          )}

          {/* Localhost Bypass Info */}
          {isLocalhost && status === 'verified' && (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <p className="text-xs text-white/30 italic">
                Localhost detected. Turnstile CAPTCHA is automatically bypassed for development.
              </p>
            </div>
          )}
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-pink-500/20 to-transparent" />
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TurnstileWidget;

